/**
 * Agency Settings Tab Component
 * Full featured agency information management
 */
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Mail,
  Phone,
  MapPin,
  Save,
  Globe,
  FileText,
  Link2,
  Copy,
  ExternalLink,
  Users,
  Heart,
  CheckCircle,
  Upload,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AgencySettingsTab({
  settings,
  onChange,
  onSave,
  saving,
  agencyCode
}) {
  const [logo, setLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      setLogoLoading(true);
      try {
        const response = await axios.get(`${API}/settings/logo`);
        if (response.data.logo) {
          setLogo(response.data.logo);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      } finally {
        setLogoLoading(false);
      }
    };
    fetchLogo();
  }, []);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        // Upload to backend
        await axios.post(`${API}/settings/logo`, { logo: base64 });
        setLogo(base64);
        toast.success("Logo uploaded successfully!");
        setUploadingLogo(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error("Failed to upload logo");
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/settings/logo`);
      setLogo(null);
      toast.success("Logo deleted");
    } catch (error) {
      console.error("Failed to delete logo:", error);
      toast.error("Failed to delete logo");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="agency-settings-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Agency Information</h2>
          <p className="text-sm text-slate-500">Update your agency details and contact information</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Logo Upload Section */}
      <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <ImageIcon className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-semibold text-slate-900">Agency Logo</h3>
            <p className="text-xs text-slate-500">Upload your logo to appear on forms and documents (PNG, JPG - max 2MB)</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {logoLoading ? (
            <div className="w-32 h-20 bg-slate-200 rounded-lg flex items-center justify-center">
              <span className="text-sm text-slate-500">Loading...</span>
            </div>
          ) : logo ? (
            <div className="relative group">
              <img 
                src={logo} 
                alt="Agency Logo" 
                className="max-w-[150px] max-h-[80px] object-contain border border-slate-200 rounded-lg bg-white p-2"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleDeleteLogo}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="w-32 h-20 bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
              <span className="text-xs text-slate-400">No logo</span>
            </div>
          )}
          
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleLogoUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
              className="gap-2"
              data-testid="upload-logo-btn"
            >
              <Upload className="w-4 h-4" />
              {uploadingLogo ? "Uploading..." : logo ? "Change Logo" : "Upload Logo"}
            </Button>
            <p className="text-xs text-slate-400 mt-1">Recommended: 300x100 pixels</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Agency Name</Label>
            <Input
              value={settings.name || ""}
              onChange={(e) => onChange({ ...settings, name: e.target.value })}
              placeholder="Your Agency Name"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                value={settings.email || ""}
                onChange={(e) => onChange({ ...settings, email: e.target.value })}
                placeholder="agency@example.com"
                className="pl-10"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={settings.phone || ""}
                  onChange={(e) => onChange({ ...settings, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fax</Label>
              <Input
                value={settings.fax || ""}
                onChange={(e) => onChange({ ...settings, fax: e.target.value })}
                placeholder="(555) 123-4568"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Website</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={settings.website || ""}
                onChange={(e) => onChange({ ...settings, website: e.target.value })}
                placeholder="https://www.example.com"
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                value={settings.address || ""}
                onChange={(e) => onChange({ ...settings, address: e.target.value })}
                placeholder="123 Main Street"
                className="pl-10"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={settings.city || ""}
                onChange={(e) => onChange({ ...settings, city: e.target.value })}
                placeholder="Austin"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={settings.state || ""}
                onChange={(e) => onChange({ ...settings, state: e.target.value })}
                placeholder="TX"
              />
            </div>
            <div className="space-y-2">
              <Label>ZIP</Label>
              <Input
                value={settings.zip_code || ""}
                onChange={(e) => onChange({ ...settings, zip_code: e.target.value })}
                placeholder="78701"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>NPI Number</Label>
              <Input
                value={settings.npi || ""}
                onChange={(e) => onChange({ ...settings, npi: e.target.value })}
                placeholder="1234567890"
              />
            </div>
            <div className="space-y-2">
              <Label>Tax ID</Label>
              <Input
                value={settings.tax_id || ""}
                onChange={(e) => onChange({ ...settings, tax_id: e.target.value })}
                placeholder="12-3456789"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Medicaid Provider Number</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={settings.medicaid_provider_number || ""}
                  onChange={(e) => onChange({ ...settings, medicaid_provider_number: e.target.value })}
                  placeholder="Enter Medicaid Provider #"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>License Number</Label>
              <Input
                value={settings.license_number || ""}
                onChange={(e) => onChange({ ...settings, license_number: e.target.value })}
                placeholder="ABC123456"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Public Application Links Section */}
      <PublicLinksSection agencyCode={agencyCode} />
    </div>
  );
}

// Public Links Section Component
function PublicLinksSection({ agencyCode }) {
  const [copiedLink, setCopiedLink] = useState(null);
  const baseUrl = window.location.origin;
  
  const jobApplicationLink = `${baseUrl}/apply/${agencyCode || 'your-agency-code'}`;
  const clientReferralLink = `${baseUrl}/refer/${agencyCode || 'your-agency-code'}`;

  const copyToClipboard = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(type);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  if (!agencyCode) {
    return null;
  }

  return (
    <div className="mt-8 pt-6 border-t border-slate-200">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-primary" />
          Public Application Links
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Share these links on your website to collect job applications and client referrals directly into your system.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Job Application Link */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Employee Application</h4>
              <p className="text-xs text-slate-500">For potential caregivers & staff</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input 
              value={jobApplicationLink} 
              readOnly 
              className="text-sm bg-white"
            />
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => copyToClipboard(jobApplicationLink, 'job')}
              className="shrink-0"
            >
              {copiedLink === 'job' ? (
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <a href={jobApplicationLink} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="icon" className="shrink-0">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Submissions appear in: HR & Payroll → Job Applications
          </p>
        </div>

        {/* Client Referral Link */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Client Referral</h4>
              <p className="text-xs text-slate-500">For new client referrals</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input 
              value={clientReferralLink} 
              readOnly 
              className="text-sm bg-white"
            />
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => copyToClipboard(clientReferralLink, 'referral')}
              className="shrink-0"
            >
              {copiedLink === 'referral' ? (
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <a href={clientReferralLink} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="icon" className="shrink-0">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          </div>
          <p className="text-xs text-emerald-600 mt-2">
            Submissions appear in: Referrals → Pending
          </p>
        </div>
      </div>

      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>Tip:</strong> Add these links to your website&apos;s &quot;Careers&quot; and &quot;Request Services&quot; pages. 
          When applicants or referrers submit forms, they&apos;ll automatically appear in your dashboard for review.
        </p>
      </div>
    </div>
  );
}
